/**
 * EXAMPLE _map DATA STRUCTURE - things are a bit weird, because sometimes a job may not exist for a line.
 * 
 * _map = {
 * 		"BNS Midstream" {
 * 			"jobLine": {
 * 				"34011111": {
 * 					"My Pipeline": {
 * 						"`la": lineAggObject,
 * 						"`j": jobObject,
 * 						"`sm": {
 * 							"DEPOL": depolSurveyObject,
 * 							"ON-OFF": onoffSurveyObject,
 * 						},
 * 					}
 * 				}
 * 			},
 * 			"lineJob": {
 * 				"My Pipeline": {
 * 					"`la": lineAggObject,
 * 					"`jm": {
 * 						"34011111": {
 * 							"`j": jobObject,
 *	 						"`sm": {
 * 								"DEPOL": depolSurveyObject,
 * 								"ON-OFF": onoffSurveyObject,
 * 							},
 * 						}
 * 					}
 * 				}
 * 			},
 * 		}
 * }
 */

/**
 * EXAMPLE LINE_AGG DATA STRUCTURE
 * const LINE_AGG = {
    "tblRowKey": "tbl-row-31-Testing Survey Dates",
    "id": 1880,
    "lineId": 2393,
    "lineExternalId": "a948aec2-beaa-4e1a-97e3-ac13c2d3fe23",
    "lineName": "Testing Survey Dates",
    "customerId": 31,
    "customerName": "Targa",
    "jobs": [
        {
            "ct": "NA",
            "id": 959,
            "pm": "NA",
            "end": "2026-04-30T13:00:00",
            "start": "2026-04-30T13:00:00",
            "surveys": [
                {
                    "id": 3756,
                    "end": "2026-04-30T13:00:00",
                    "type": "ON_OFF",
                    "miles": 0,
                    "start": "2026-04-30T13:00:00",
                    "status": "COMPLETE",
                    "bc_count_on": "0",
                    "bc_count_off": "0",
                    "reportdelivery": null,
                    "total_count_on": "0",
                    "total_count_off": "0",
                    "bc_percentage_on": 0,
                    "bc_percentage_off": 0,
                    "disable_auto_align": true,
                    "fallback_sort_date": "2026-04-30T13:00:00+00:00",
                    "externalId": "2359dcc0-1100-11f1-950c-ddd6e11b9fa8",
                    "typeId": 4,
                    "totalCount": "0",
                    "bcCount": "0",
                    "bcPercentage": 0,
                    "sortEpoch": 1777572000000
                }
            ],
            "scanline": true,
            "job_line_id": 2325,
            "line_state_count": 0,
            "psql_insert_date": "2026-02-23T21:38:44.595934+00:00",
            "fallback_sort_date": "2026-04-30T13:00:00+00:00",
            "report_state_count": 0,
            "is_created_by_customer": true,
            "jobNumber": 902000000000007,
            "entityId": 547,
            "entityName": "LAA-Louisiana Area Assets",
            "areaId": 5,
            "areaName": "CYN-Cayenne Pipeline",
            "pipeId": 5,
            "pipeName": "Charlie 6",
            "regulatoryStatusId": 2,
            "regulatoryStatusName": "Gas - Unregulated",
            "pmId": 186,
            "ctId": 186,
            "sortEpoch": 1777572000000,
            "miles": 0,
            "totalCount": "0",
            "bcCount": "0",
            "bcPercentage": 0
        }
    ],
    "tags": null,
    "lastUpdated": "1771882772",
    "fallbackSortDate": "2026-04-30T13:00:00.000Z",
    "jobNumbers": "902000000000007",
    "entities": "LAA-Louisiana Area Assets",
    "areas": "CYN-Cayenne Pipeline",
    "pipeIDs": "Charlie 6",
    "regulatoryStatuses": "Gas - Unregulated",
    "ctIds": "186",
    "cts": "NA",
    "pmIds": "186",
    "pms": "NA",
    "surveyTypes": "ON_OFF",
    "miles": 0,
    "totalCount": "0",
    "bcCount": "0",
    "bcPercentage": 0,
    "sortEpoch": 1777525200000,
    "start": "2026-04-30",
    "end": "2026-04-30",
    "txtStartEnd": "2026-04-30"
}
 */



/** ************************************************
 * CREATE LOOKUP TABLE
 */

// we mix and match linenames and jobnumbers as keys, as well as pointers to objects such as lineAgg, job, survey
// so we will use characters that are highly unlikely to appear in user created data
const KEYS = {
	LINE_AGG: '`la',
	JOB_MAP: '`jm',
	JOB: '`j',
	SURVEY_MAP: '`sm',
}

const _addCustomer = (_map, customerName) => {
	if (!_map[customerName]) {
		_map[customerName] = {
			jobLine: {},
			lineJob: {},
		}
	}
};

const correctSurveyType = (survey) => {
	if (survey.type === 'ON_OFF') {
		survey.type = 'ON-OFF';
	}
};

const _createSurveyMap = (surveys = []) => {
	return surveys.reduce((acc, s) => {
		correctSurveyType(s);
		const { type } = s;
		acc[type] = s;
		return acc;
	}, {})
};

const _addJobLine = (_mapCustomer, lineAgg, job, _mapSurveys) => {
	if (!_mapCustomer.jobLine) {
		throw new Error('_addJobLine(): prop jobLine should always exist, but does not');
	}
	if (!lineAgg) {
		throw new Error('_addJobLine(): lineAgg should always exist, but does not');
	}
	if (!lineAgg?.lineName) {
		throw new Error('_addJobLine(): lineAgg.lineName should always exist, but does not');
	}
	const root = _mapCustomer.jobLine;
	const { lineName } = lineAgg;
	const { jobNumber } = job || {};
	const hasJob = !!jobNumber;

	if (hasJob) {
		root[jobNumber] = root[jobNumber] || {};
		root[jobNumber][lineName] = {
			[KEYS.LINE_AGG]: lineAgg,
			[KEYS.JOB]: job,
			[KEYS.SURVEY_MAP]: _mapSurveys
		};
	}
}

const _addLineJob = (_mapCustomer, lineAgg, job, _mapSurveys) => {
	if (!_mapCustomer.lineJob) {
		throw new Error('_addLineJob(): prop lineJob should always exist, but does not');
	}
	if (!lineAgg) {
		throw new Error('_addJobLine(): lineAgg should always exist, but does not');
	}
	if (!lineAgg?.lineName) {
		throw new Error('_addJobLine(): lineAgg.lineName should always exist, but does not');
	}
	const root = _mapCustomer.lineJob;
	const { lineName } = lineAgg;
	const { jobNumber } = job || {};
	const hasJob = !!jobNumber;

	root[lineName] = root[lineName] || {
		[KEYS.LINE_AGG]: lineAgg,
		[KEYS.JOB_MAP]: root[lineName]?.[KEYS.JOB_MAP] || {}
	};

	if (hasJob) {
		root[lineName][KEYS.JOB_MAP][jobNumber] = {
			[KEYS.JOB]: job,
			[KEYS.SURVEY_MAP]: _mapSurveys
		}
	}
	
}

const _mainAdd = (_map, lineAgg, job) => {
	const { customerName } = lineAgg;
	const { surveys } = job || {};
	
	_addCustomer(_map, customerName);

	const _mapSurveys = _createSurveyMap(surveys);

	_addJobLine(_map[customerName], lineAgg, job, _mapSurveys);
	_addLineJob(_map[customerName], lineAgg, job, _mapSurveys);
}

const createMap = (linesAggs) => {
	const _map = {};
	linesAggs.forEach((la) => {
		const { jobs = [] } = la;
		if (!jobs.length) {
			_mainAdd(_map, la);
		}
		jobs.forEach((job) => {
			_mainAdd(_map, la, job);
		})
	});
	return _map;
}

/** ************************************************
 * READ FROM LOOKUP TABLE
 */

const _getLineAgg = (_map, customerName, lineName, jobNumber) => {
	if (jobNumber) {
		return _map?.[customerName]?.jobLine?.[jobNumber]?.[lineName]?.[KEYS.LINE_AGG];
	}
	return _map?.[customerName]?.lineJob?.[KEYS.LINE_AGG];
};

const _getJob = (_map, customerName, lineName, jobNumber) => {
	if (!jobNumber) {
		return undefined;
	}
	return _map?.[customerName]?.jobLine?.[jobNumber]?.[lineName]?.[KEYS.JOB];
}

const _getSurvey = (_map, customerName, lineName, jobNumber, surveyType) => {
	if (!jobNumber) {
		return undefined;
	}

	return _map?.[customerName]?.jobLine?.[jobNumber]?.[lineName]?.[KEYS.SURVEY_MAP]?.[surveyType];
}

const getScanlineMetaValue = (_map, customerName, lineName, jobNumber, prop) => {
	const job = _getJob(_map, customerName, lineName, jobNumber);

	switch (prop) {
		case 'entity.name':
			return job?.entityName;
		case 'area.name':
			return job?.areaName;
		case 'pipe.name':
			return job?.pipeName;
		case 'regulatoryStatus.name':
			return job?.regulatoryStatusName;
		case 'projectManager.name':
			return job?.pm;
		case 'corrosionTechnician.name':
			return job?.ct;
		default:
			return undefined;
	}
}

const doesScanlineSurveyExist = (_map, customerName, lineName, jobNumber, surveyType) => {
	return !!_getSurvey(_map, customerName, lineName, jobNumber, surveyType)
}

const doesScanlineLineExist = (_map, customerName, lineName) => {
	return !!_getLineAgg(_map, customerName, lineName)
}

const doesScanlineJobExist = (_map, customerName, jobNumber) => {
	const _jobLine = _map[customerName]?.jobLine || {};
	const root = _jobLine[jobNumber] || {};
	const lineNames = Object.keys(root);
	return lineNames.some((lineName) => (!!_getJob(_map, customerName, lineName, jobNumber)));
}

const doesScanlineJobLineExist = (_map, customerName, lineName, jobNumber) => {
	const lineAgg = _getLineAgg(_map, customerName, lineName, jobNumber);
	const job = _getJob(_map, customerName, lineName, jobNumber);
	return !!lineAgg && !!job;
}

export const LS_SUB_ITEMS_MAP_KEYS = KEYS;

const LINES_AGGS_UTILS = {
	createMap,
	getScanlineMetaValue,

	doesScanlineJobLineExist,
	doesScanlineJobExist, 
	doesScanlineLineExist,
	doesScanlineSurveyExist,

	LS_SUB_ITEMS_MAP_KEYS
};

export default LINES_AGGS_UTILS;